import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {
  "pageTitle": "Icons Design Guidelines",
  "title": "Icons",
  "order": 2
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const DocsHeading = makeShortcode("DocsHeading");
const LeadParagraph = makeShortcode("LeadParagraph");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
<DocsHeading to="/api/icon/" type="design" mdxType="DocsHeading">
  Icons
</DocsHeading>
<LeadParagraph mdxType="LeadParagraph">
  Icons are visual symbols used to represent objects or actions. They
  communicate messages at a glance, clarify the nature of an interactive
  element, and draw attention to important information.
</LeadParagraph>
<p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1035px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "35.13513513513513%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAIAAACHqfpvAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA7klEQVQY0yWQRw6FQAxDuf/dAAGiCz69dwTs0H9Dshh5Esdxoh3HsSzLNE3btoH3fZ/neV1XgHypnue5fwGev4B/37fWdV0URaZpxnH8+8JxHMFINE0ThmHf91mWpWlqWZau667rGoZxXZdWVZVt257nARCClyQJcmR83w+CgLcoCkpt28pkxjJMNZNFm1GAYRjgyXwA0wCUyNPDixdoYkc1g9DGBlQa8AzI8xyTZVlih5f1WHgcx/d9qcLHv9oZD5THL6CSRQ6GmJQTsAKZuq6pMgAaHNXMJSFhiWsDIMlVEcUkJxCqLCVYyM/z/AEpQHepX3RayAAAAABJRU5ErkJggg==')",
            "backgroundSize": "cover",
            "display": "block"
          }
        }}></span>{`
  `}<img parentName="span" {...{
          "className": "gatsby-resp-image-image",
          "alt": " ",
          "title": " ",
          "src": "/version/2.6.2/static/533c41debc0c597c0852d4c5bc8cad6a/e3189/icons-intro.png",
          "srcSet": ["/version/2.6.2/static/533c41debc0c597c0852d4c5bc8cad6a/a2ead/icons-intro.png 259w", "/version/2.6.2/static/533c41debc0c597c0852d4c5bc8cad6a/6b9fd/icons-intro.png 518w", "/version/2.6.2/static/533c41debc0c597c0852d4c5bc8cad6a/e3189/icons-intro.png 1035w", "/version/2.6.2/static/533c41debc0c597c0852d4c5bc8cad6a/aa440/icons-intro.png 1500w"],
          "sizes": "(max-width: 1035px) 100vw, 1035px",
          "style": {
            "width": "100%",
            "height": "100%",
            "margin": "0",
            "verticalAlign": "middle",
            "position": "absolute",
            "top": "0",
            "left": "0"
          },
          "loading": "lazy"
        }}></img>{`
    `}</span></p>
<h2>{`Icon Resources`}</h2>
<p>{`React Magma primarily uses the beautiful icons from Material Design. Specifically we use the `}<a parentName="p" {...{
        "href": "https://material.io/resources/icons/?style=round"
      }}>{`rounded variant`}</a>{`. We also have a group of custom icons when Material doesn't have what we need, but we take great care to design those icons with the same `}<a parentName="p" {...{
        "href": "https://material.io/design/iconography/system-icons.html#design-principles"
      }}>{`principles`}</a>{` that Google does.`}</p>
<p>{`We do our best to keep up with any updates that happen to Material Design's icons, and that includes updating the `}<a parentName="p" {...{
        "href": "https://www.dropbox.com/sh/kt8oaf7am443794/AAArcOLft67q5Fk90wcXSmida?dl=0"
      }}>{`Sketch icon library you can download`}</a>{`. If you are a designer at Cengage, and you are a current Abstract user, then you'll find the Sketch libraries there instead.`}</p>
<hr></hr>
<h2>{`Sizing`}</h2>
<p>{`When choosing a size for your icon, you should choose a size that is evenly divisible by 8 in order for it to harmonize with the spacing system. If you are pairing the icon with a line of text, try to choose a size that matches the line-height of that text.`}</p>
<p>{`The first example below shows a sample of acceptable icon sizes: 16px, 20px, 24px, 32px, 48px, 64px, 80px. The one exception to the sizing rule is the 20px icon. It is divisible by the smaller sub-unit of 4px, and is primarily for pairing with 14px text with a line-height of 20px.`}</p>
<p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1035px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "26.640926640926644%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAFCAIAAADKYVtkAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAqElEQVQY002QvQ6EIBCEff9nsqG0scBCG2JhLIhGhBNPxeK+c3M5JmEz+zcDFO8fjuOQKOQ8z33fr+uCSCqtbduWZZGZgkMeY3w98N6v6xpCGIYhpdR13TiObFZV1bYtFa11WZbzPKP7XY4Pcgn4NE20+7631rJc17Ux5r7vpmmUUhhQLLgAblhJFIKKc44WQwgxR10IUVr/a+fAmdciwYTs5IQIl2/6ACzbGnCpCIsDAAAAAElFTkSuQmCC')",
            "backgroundSize": "cover",
            "display": "block"
          }
        }}></span>{`
  `}<img parentName="span" {...{
          "className": "gatsby-resp-image-image",
          "alt": " ",
          "title": " ",
          "src": "/version/2.6.2/static/042cff760db3d003c8273e1b561af25c/e3189/icon-sizes.png",
          "srcSet": ["/version/2.6.2/static/042cff760db3d003c8273e1b561af25c/a2ead/icon-sizes.png 259w", "/version/2.6.2/static/042cff760db3d003c8273e1b561af25c/6b9fd/icon-sizes.png 518w", "/version/2.6.2/static/042cff760db3d003c8273e1b561af25c/e3189/icon-sizes.png 1035w", "/version/2.6.2/static/042cff760db3d003c8273e1b561af25c/aa440/icon-sizes.png 1500w"],
          "sizes": "(max-width: 1035px) 100vw, 1035px",
          "style": {
            "width": "100%",
            "height": "100%",
            "margin": "0",
            "verticalAlign": "middle",
            "position": "absolute",
            "top": "0",
            "left": "0"
          },
          "loading": "lazy"
        }}></img>{`
    `}</span></p>
<p>{`This example illustrates how 12px, 14px, 16px, and 20px text strings are paired with icons sized to match their line-heights.`}</p>
<p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1035px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "71.81467181467181%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAOCAIAAACgpqunAAAACXBIWXMAAAsTAAALEwEAmpwYAAAB+klEQVQoz3VTXXPSQBTtr/dZX5xx1NEZsA4dqGLpi0o7og00mlK+CkmF8A1pQiDs7t3dJIg3gY5V8M7mzN1s7p5z92wOeByzJZtRbm+ROgQQbXfBGeOUcSG44LtxgA8D+FhrHxbrx5qR/t7KVrsZzciUOx+UslwQNrBItcMJjbbYLcagjC0JpQwIZRQQKWEYIBjgKiA5wH7maFlIkD7q2yCLpwT4ksaypeCB/1/Zp2UjqTSOK+YbpZapmEfabUrVD1U9dVYSqGF0R2+6cfP7mH9OnJuBZVjzCCdua+w0h3fNka33xuARmHtsOuMM9stehcF6FaxDf70K1+Gf5NcqCH25lS3lnmIAKPesi860aFol01I6U7XvfLsdXnbtr8agUGkR1+PIb7v/kG97zl3pr861ox9GSm0lCtfZxjDx5QrzpFJPfi56M4/bLqm2sYWHht1bhQcLnIAgMS4Zx68w9wjz0OGNVagfC2GnZy597gcbw3g8QES2CT/gkXmSCT/SjLtEzYu/ZOebg8y1eVIfZGv9ePROW+P31W5aM3LNEV67p9m83h0HFNjEATyCWPy2OF1qPDkpvDgvJy6NZ5/U52fa64vGy0Ll8bt8oqTj+0dvc432MCSM6H20PSKHe9keyDnIxQa5vwB/DgKTzdRZsqnj4m2N/pMHsn8DjtoBzlA3UbYAAAAASUVORK5CYII=')",
            "backgroundSize": "cover",
            "display": "block"
          }
        }}></span>{`
  `}<img parentName="span" {...{
          "className": "gatsby-resp-image-image",
          "alt": " ",
          "title": " ",
          "src": "/version/2.6.2/static/7db7329d77a37ffb74c56fefa8e2eec7/e3189/icon-text-balance.png",
          "srcSet": ["/version/2.6.2/static/7db7329d77a37ffb74c56fefa8e2eec7/a2ead/icon-text-balance.png 259w", "/version/2.6.2/static/7db7329d77a37ffb74c56fefa8e2eec7/6b9fd/icon-text-balance.png 518w", "/version/2.6.2/static/7db7329d77a37ffb74c56fefa8e2eec7/e3189/icon-text-balance.png 1035w", "/version/2.6.2/static/7db7329d77a37ffb74c56fefa8e2eec7/aa440/icon-text-balance.png 1500w"],
          "sizes": "(max-width: 1035px) 100vw, 1035px",
          "style": {
            "width": "100%",
            "height": "100%",
            "margin": "0",
            "verticalAlign": "middle",
            "position": "absolute",
            "top": "0",
            "left": "0"
          },
          "loading": "lazy"
        }}></img>{`
    `}</span></p>
<div {...{
      "className": "two-col"
    }}>{`
  `}<div parentName="div" {...{
        "className": "row"
      }}>{`
    `}<div parentName="div" {...{
          "className": "col col-1"
        }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
              "className": "gatsby-resp-image-wrapper",
              "style": {
                "position": "relative",
                "display": "block",
                "marginLeft": "auto",
                "marginRight": "auto",
                "maxWidth": "748px"
              }
            }}>{`
      `}<span parentName="span" {...{
                "className": "gatsby-resp-image-background-image",
                "style": {
                  "paddingBottom": "65.63706563706563%",
                  "position": "relative",
                  "bottom": "0",
                  "left": "0",
                  "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAu0lEQVQoz82SMQ6EIBBFva019/EKxs4WtETtaUxspDBaubgoRvcHNoQtLMw2voLAD29mSIjefxA9Q16W5WW5LcNc1/U8z+M4cMTehVprtwGXMi7N85ymKWMM5jRNLlRKYTXGbNuGPCzxlXEDPTnncRwTQoZhEEJQSvM8r6qqKApULMtSSokS3o/CmcdxTJIkyzIk6Ny2bdd1fd/Dqeu6aRqEYfOfN6PqbtEWP6qxXI7tfWXxx5DHfpK7fAAmQuMVlBLtygAAAABJRU5ErkJggg==')",
                  "backgroundSize": "cover",
                  "display": "block"
                }
              }}></span>{`
  `}<img parentName="span" {...{
                "className": "gatsby-resp-image-image",
                "alt": "icon text balance correct",
                "title": "icon text balance correct",
                "src": "/version/2.6.2/static/5e2724e2aa985dc123960a0e093f09a9/f8915/icon-text-balance-correct.png",
                "srcSet": ["/version/2.6.2/static/5e2724e2aa985dc123960a0e093f09a9/a2ead/icon-text-balance-correct.png 259w", "/version/2.6.2/static/5e2724e2aa985dc123960a0e093f09a9/6b9fd/icon-text-balance-correct.png 518w", "/version/2.6.2/static/5e2724e2aa985dc123960a0e093f09a9/f8915/icon-text-balance-correct.png 748w"],
                "sizes": "(max-width: 748px) 100vw, 748px",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                },
                "loading": "lazy"
              }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                "className": "title title-correct"
              }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            Maintain the balance of icons with text by using the line-height as
            a guide.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
          "className": "col col-2"
        }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
              "className": "gatsby-resp-image-wrapper",
              "style": {
                "position": "relative",
                "display": "block",
                "marginLeft": "auto",
                "marginRight": "auto",
                "maxWidth": "748px"
              }
            }}>{`
      `}<span parentName="span" {...{
                "className": "gatsby-resp-image-background-image",
                "style": {
                  "paddingBottom": "65.63706563706563%",
                  "position": "relative",
                  "bottom": "0",
                  "left": "0",
                  "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABOklEQVQoz41Su6qDQBTMV1qK+DWCTT7C1kYQLAw2FgGvRBBEIdjEymd8JXp93cElXpOYy51imePunNkz6+7+jNvtVtf1/X/YrQvI2rbt+76qqqXXC7bF2JimyTAMSZJAULYzvme0D2yI4TmOoyiKDMPQNL3f73EuiqI8z5MkieP4er2WZYlybf4rhhvP8xzHKYqCFihd19U07XA4YLVtG0TXdeibpnkSox9GVVWVZVmKogRBwEe4hWFInGHrOE4QBBvOi/nlcrEsi8wMBzItVsKBj2kjZJJ2URRLR4DkTPhHMTmNcwgPHG5oNAxD/wD4X2I4p2lqmiaI53kYAfx0Ovm+j/wwNm73GthanGUZNDiB+fHsXzPO57Msy8fjcSPtNbBHrodGwwNd12EiktzHay8v9/5vEtn6qX4ApYjS6d4EYnoAAAAASUVORK5CYII=')",
                  "backgroundSize": "cover",
                  "display": "block"
                }
              }}></span>{`
  `}<img parentName="span" {...{
                "className": "gatsby-resp-image-image",
                "alt": "icon text balance incorrect",
                "title": "icon text balance incorrect",
                "src": "/version/2.6.2/static/63597b2e5660581dcc2295ec9e71e4e0/f8915/icon-text-balance-incorrect.png",
                "srcSet": ["/version/2.6.2/static/63597b2e5660581dcc2295ec9e71e4e0/a2ead/icon-text-balance-incorrect.png 259w", "/version/2.6.2/static/63597b2e5660581dcc2295ec9e71e4e0/6b9fd/icon-text-balance-incorrect.png 518w", "/version/2.6.2/static/63597b2e5660581dcc2295ec9e71e4e0/f8915/icon-text-balance-incorrect.png 748w"],
                "sizes": "(max-width: 748px) 100vw, 748px",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                },
                "loading": "lazy"
              }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                "className": "title title-incorrect"
              }}>{`Incorrect`}</p>{`
          `}<p parentName="figcaption">{`
            Don't let icons get too large or too small when paired with text.
            Use the line-height of the text as a guide.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
  `}</div>
</div>
<hr></hr>
<h2>{`Color`}</h2>
<p>{`Our icons are simple monochromatic symbols, and any color from the color palette may be applied to one. But responsible use of color is important.`}</p>
<p>{`The color of an icon needs to hit a minimum color contrast ratio of 3:1 with the background it appears on. The color of the icon can help reflect the importance or nature of the action the icon is representing, but shouldn't rely on color alone. Text labels or tooltips are still important elements that can help make the action clearer to users.`}</p>
<div {...{
      "className": "two-col"
    }}>{`
  `}<div parentName="div" {...{
        "className": "row"
      }}>{`
    `}<div parentName="div" {...{
          "className": "col col-1"
        }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
              "className": "gatsby-resp-image-wrapper",
              "style": {
                "position": "relative",
                "display": "block",
                "marginLeft": "auto",
                "marginRight": "auto",
                "maxWidth": "748px"
              }
            }}>{`
      `}<span parentName="span" {...{
                "className": "gatsby-resp-image-background-image",
                "style": {
                  "paddingBottom": "66.02316602316603%",
                  "position": "relative",
                  "bottom": "0",
                  "left": "0",
                  "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABEklEQVQoz41S3WqDMBj1ZXexd/BysAfwLXyAMhD2czNsh6hd0QtXZVUEdbJoE52ZsztNKYOsoofwkcB3cs75EqUdR3PGWIMinb8Euq5D/T6Dcz5Nhkjf97quG4ZBCLFt2/d9z/PCMMRds5Rd1wWBUroTiOM4TdNZZGAYBuhLti8ml22jLwgCCOLIGDsJcoFZtlVV1TQNmZ8FHMexLAv1v3OZDIWVab6u17gmy7I8z8uyjKIoSZJp8inzT9+DzJFWrLHXUqRvgSkVq5dys2lZQ4uCfRJGCNvvZwyMMX44eFfX25tb7PP7h+LxKVvcfZjLdpLcooF31du2ft/BNquqhtKmrlFnfc+jczwyMovR/9VL+AV2p9+DtPaEogAAAABJRU5ErkJggg==')",
                  "backgroundSize": "cover",
                  "display": "block"
                }
              }}></span>{`
  `}<img parentName="span" {...{
                "className": "gatsby-resp-image-image",
                "alt": "icon color correct",
                "title": "icon color correct",
                "src": "/version/2.6.2/static/7288f3bcc262a383d32019b21e9f149e/f8915/icon-color-correct.png",
                "srcSet": ["/version/2.6.2/static/7288f3bcc262a383d32019b21e9f149e/a2ead/icon-color-correct.png 259w", "/version/2.6.2/static/7288f3bcc262a383d32019b21e9f149e/6b9fd/icon-color-correct.png 518w", "/version/2.6.2/static/7288f3bcc262a383d32019b21e9f149e/f8915/icon-color-correct.png 748w"],
                "sizes": "(max-width: 748px) 100vw, 748px",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                },
                "loading": "lazy"
              }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                "className": "title title-correct"
              }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            It's nearly always a good idea to match the color of the icon with
            the text label, but sometimes you may want the icon to be a list
            more subtle than the text.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
          "className": "col col-2"
        }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
              "className": "gatsby-resp-image-wrapper",
              "style": {
                "position": "relative",
                "display": "block",
                "marginLeft": "auto",
                "marginRight": "auto",
                "maxWidth": "748px"
              }
            }}>{`
      `}<span parentName="span" {...{
                "className": "gatsby-resp-image-background-image",
                "style": {
                  "paddingBottom": "66.02316602316603%",
                  "position": "relative",
                  "bottom": "0",
                  "left": "0",
                  "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAgElEQVQoz82SOwrAIBBEvf/VRBHxDIqIVmr8NBm0kQTyIU1esUzhzA6LZPsA+Z855/xpcx6s4t4838UYW2u991rrFDfmUor33hiTUmKMUUqFEJxzaCmlUgpBa4Xj5hCCcw5mTGstgiAwtdbIRfpV7TKAaINZuw8gXlz7zP8/yRN26bzsANS/JrMAAAAASUVORK5CYII=')",
                  "backgroundSize": "cover",
                  "display": "block"
                }
              }}></span>{`
  `}<img parentName="span" {...{
                "className": "gatsby-resp-image-image",
                "alt": "icon color too light",
                "title": "icon color too light",
                "src": "/version/2.6.2/static/8eb1464ea2aa7f1588d3ec8170f39883/f8915/icon-color-too-light.png",
                "srcSet": ["/version/2.6.2/static/8eb1464ea2aa7f1588d3ec8170f39883/a2ead/icon-color-too-light.png 259w", "/version/2.6.2/static/8eb1464ea2aa7f1588d3ec8170f39883/6b9fd/icon-color-too-light.png 518w", "/version/2.6.2/static/8eb1464ea2aa7f1588d3ec8170f39883/f8915/icon-color-too-light.png 748w"],
                "sizes": "(max-width: 748px) 100vw, 748px",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                },
                "loading": "lazy"
              }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                "className": "title title-incorrect"
              }}>{`Incorrect`}</p>{`
          `}<p parentName="figcaption">{`
            In this example, the icon only hits a contrast ratio of 1.7:1.
            Despit the fact there is a text label, the icons should still hit a
            minimum of 3:1.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
  `}</div>{`
  `}<div parentName="div" {...{
        "className": "row"
      }}>{`
    `}<div parentName="div" {...{
          "className": "col col-1"
        }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
              "className": "gatsby-resp-image-wrapper",
              "style": {
                "position": "relative",
                "display": "block",
                "marginLeft": "auto",
                "marginRight": "auto",
                "maxWidth": "748px"
              }
            }}>{`
      `}<span parentName="span" {...{
                "className": "gatsby-resp-image-background-image",
                "style": {
                  "paddingBottom": "66.02316602316603%",
                  "position": "relative",
                  "bottom": "0",
                  "left": "0",
                  "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAf0lEQVQoz83SMQvCMBAF4P7/P2X3boLFQamN0DRIIobk5S6NoBUKHZO69Ka3fHD3uCr8MdVeMTZgAOuMEowpJsA777/uHTky5WIm7sbzZThKbetWHE59cx0oE1MgqYV1L49wfzxvyihjKf/miRMFnkPkVLT2r2QsbRUXtv8P+wBASO5GdKBcWgAAAABJRU5ErkJggg==')",
                  "backgroundSize": "cover",
                  "display": "block"
                }
              }}></span>{`
  `}<img parentName="span" {...{
                "className": "gatsby-resp-image-image",
                "alt": "icon mixed colors",
                "title": "icon mixed colors",
                "src": "/version/2.6.2/static/9568d4c63fba7c86eb23185bc1dbd467/f8915/icon-mixed-colors.png",
                "srcSet": ["/version/2.6.2/static/9568d4c63fba7c86eb23185bc1dbd467/a2ead/icon-mixed-colors.png 259w", "/version/2.6.2/static/9568d4c63fba7c86eb23185bc1dbd467/6b9fd/icon-mixed-colors.png 518w", "/version/2.6.2/static/9568d4c63fba7c86eb23185bc1dbd467/f8915/icon-mixed-colors.png 748w"],
                "sizes": "(max-width: 748px) 100vw, 748px",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                },
                "loading": "lazy"
              }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                "className": "title title-incorrect"
              }}>{`Incorrect`}</p>{`
          `}<p parentName="figcaption">{`
            Do not mix colors when combining an icon with text, unless you are
            using different shades of the same color or using our default text
            color (#3F3F3F).
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
          "className": "col col-2"
        }}></div>{`
  `}</div>
</div>
<hr></hr>
<h2>{`Alignment`}</h2>
<p>{`When positioning an icon next to text you should always center-align them.`}</p>
<undefined><div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "748px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "64.09266409266408%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAyklEQVQoz9WS0QqCMBSGfdwuomfoInoBX8HLLoIgsGIWdFFB5iN4p04c25jTTcQOk4JCDOomP8bh3zj/OZzDrOIHrP8w5wYpJWjZRZ+5qiqlFETQIMqy1Fq3oqXbDFUhDyFk23YYhtA/TVPOeZIkhBBKKWMM4ruZCtkelhfT2Xw0niyWK7huvcN6s4N4vt5ctHeRdzxdCBeQ9rS8dPZ933EcjDG8YEOWZdA5iqIgCOI47pu5ruumaaAKaG1QBv3gw7aFEF9uezg/7A64c+Ka20/A8gAAAABJRU5ErkJggg==')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "icon text centered",
                  "title": "icon text centered",
                  "src": "/version/2.6.2/static/a082fbcd92eef6055da705adde3343dc/f8915/icon-text-centered.png",
                  "srcSet": ["/version/2.6.2/static/a082fbcd92eef6055da705adde3343dc/a2ead/icon-text-centered.png 259w", "/version/2.6.2/static/a082fbcd92eef6055da705adde3343dc/6b9fd/icon-text-centered.png 518w", "/version/2.6.2/static/a082fbcd92eef6055da705adde3343dc/f8915/icon-text-centered.png 748w"],
                  "sizes": "(max-width: 748px) 100vw, 748px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-correct"
                }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            Always center-align icons with text when they're next to each other.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "748px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "64.09266409266408%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABZElEQVQoz31Sy26CQBTlg40bPsGFG/sF/EITDSuIJjRpQmvLAjel9Ul9UlNAXjIDanrKpGRI0Rtyc4CZe+459wppNfI8P/4FXv8DPoQSEUKQHcc5n88AlFLkLMsIF/WXURXnJEkSRVHXdWDf9/HR87wwDBltHMdXmUF11+k0Go1+vx9F0WAwUBQF2DAMZE3ThsNhkiSVy4f4yJ7wSNbO/tUcBUnqhfFitZnaS3u9Xe2+Pub24/PLZPHpR0l5Ho/AV4IqGJYWxtDsN5DREYOFC6S+bQS6YgqZC0kRPLiqufA2v1wupbOgQiOkYKaUMac1miEmSuno7f2+J++9w+7btaYze7OzJrP5cg3xy62zcva84IpmkLVarWaz2et2QYWBqar6oGmmacJ5VVGedB3T4qddmbMsy+1227IsYEw4CIKoCBgxHo9d172lGQpR+3Q6sbHzu4VfqHjrMvhxjm0Cv+HXdvsHdprTO2w7rrcAAAAASUVORK5CYII=')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "icon text wrong alignment",
                  "title": "icon text wrong alignment",
                  "src": "/version/2.6.2/static/66f4212d3cfbb540bc325f958a2690df/f8915/icon-text-wrong-alignment.png",
                  "srcSet": ["/version/2.6.2/static/66f4212d3cfbb540bc325f958a2690df/a2ead/icon-text-wrong-alignment.png 259w", "/version/2.6.2/static/66f4212d3cfbb540bc325f958a2690df/6b9fd/icon-text-wrong-alignment.png 518w", "/version/2.6.2/static/66f4212d3cfbb540bc325f958a2690df/f8915/icon-text-wrong-alignment.png 748w"],
                  "sizes": "(max-width: 748px) 100vw, 748px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-incorrect"
                }}>{`Incorrect`}</p>{`
          `}<p parentName="figcaption">{`
            Don't bottom-align or top-align icons with text when they're next to
            each other.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
  `}</div>
</div>
<span {...{
        "className": "horizontal-spacer"
      }}></span></undefined>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      